import React from 'react';
import {
  ListItemAvatar,
  ListItemText,
  TableList,
} from 'app/components/ChakraTableList';
import {
  Box,
  BoxProps,
  CheckboxProps,
  Flex,
  Radio,
  Skeleton,
  SkeletonCircle,
} from '@chakra-ui/react';
import { getAvatarImage } from 'app/utils/utils';
import { formatCountryLocation } from 'app/utils/helpers';
import { DDL } from 'types/general.types';
import { SearchableProfile } from 'types/notification.types';
import { InviteType } from 'types/collaborator.types';
import { useAppSelector } from 'app/store';
import { getSearchNextlink } from 'app/store/Profile/selectors';
import { NotFoundSuggestionList } from '../SelectCollaborators/NotFoundSuggestionList';
import { suggestedCollaboratorsListSx } from './styles';

export interface SuggestedCollaborator {
  collaborator?: SearchableProfile;
  inviteeEmail?: string;
  selected?: boolean;
  disabled?: boolean;
  message?: string;
  inviteType: InviteType;
}

export interface SuggestedCollaboratorsListProps extends BoxProps {
  countriesDDL: DDL;
  suggestionlist?: SuggestedCollaborator[];
  loading: boolean;
  label: string;
  onToggle: (collaborator: SuggestedCollaborator) => void;
  loaderRef?: React.LegacyRef<HTMLDivElement>;
}

export const SuggestedCollaboratorsList: React.FC<SuggestedCollaboratorsListProps> = ({
  countriesDDL,
  suggestionlist = [],
  loading,
  label,
  onToggle,
  loaderRef,
  ...rest
}) => {
  const nextLink = useAppSelector(getSearchNextlink);
  const notFound = !loading && suggestionlist.length === 0;
  const { checkboxSx, listWrapperSx } = suggestedCollaboratorsListSx;

  return (
    <Box {...rest}>
      <TableList
        scrollable
        wrapperSx={listWrapperSx}
        data-testid="collaborator-list-items"
        columns={[
          { key: 'profile', name: label },
          { key: 'checkbox', name: '', width: 48 },
        ]}
        loadingOptions={{
          loading: !nextLink && loading,
          repeat: 4,
          loader: {
            profile: (
              <Flex gap={2} alignItems="center">
                <SkeletonCircle width={10} height={10} />
                <Box>
                  <Skeleton width={20} height={4} mb={1} />
                  <Skeleton width={32} height={4} />
                </Box>
              </Flex>
            ),
            checkbox: <Skeleton width={48} height={6} rounded="full" />,
          },
        }}
        onRowClick={({ rowData }) => {
          if (!rowData.disabled) {
            onToggle({
              ...rowData,
              selected: !rowData.selected,
            });
          }
        }}
        rows={suggestionlist.map((rowData, i) => {
          const { collaborator, inviteeEmail, selected, disabled } = rowData;
          return {
            profile: (
              <Flex gap={2}>
                <ListItemAvatar
                  src={getAvatarImage(collaborator?.profileImages)}
                  initials={collaborator?.initials}
                  colors={collaborator?.avatarColors}
                />
                <ListItemText
                  data-testid="collaborator-display-name"
                  title={collaborator?.displayName || inviteeEmail || ''}
                  primary={collaborator?.displayName || inviteeEmail || ''}
                  secondary={
                    disabled
                      ? rowData.message
                      : collaborator?.address
                      ? formatCountryLocation(countriesDDL || {}, {
                          countryCode: collaborator.address.countryCode,
                          city: collaborator.address.city,
                        })
                      : inviteeEmail
                      ? 'Add by email'
                      : ''
                  }
                  secondaryTextProps={{
                    color: 'textSubdued',
                    mt: 1,
                  }}
                />
              </Flex>
            ),
            checkbox: (
              <Radio
                isChecked={selected}
                isDisabled={disabled}
                sx={checkboxSx}
                inputProps={
                  {
                    'data-testid': `check-collaborator-${i}`,
                  } as CheckboxProps['inputProps']
                }
              />
            ),
            rowData,
            clickable: !disabled,
          };
        })}
      />
      <Box
        ref={loaderRef}
        sx={{ visibility: !loading && nextLink ? 'visible' : 'hidden' }}
      >
        <Flex gap={2} alignItems="center">
          <SkeletonCircle width={10} height={10} />
          <Box>
            <Skeleton width={20} height={4} mb={1} />
            <Skeleton width={32} height={4} />
          </Box>
        </Flex>
      </Box>
      {notFound && <NotFoundSuggestionList />}
    </Box>
  );
};
